'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import { useTranslations } from 'next-intl';

import { useFilters } from '@/src/lib/filters';
import { useCurrentUser, useFetchSavedSearches, useSavedSearches, useSaveSearchTarget } from '@/src/lib/hooks/query';
import { buildSavedSearchMatchKey, findMatchingSavedSearch } from '@/src/lib/savedSearches';
import dynamic from 'next/dynamic';
import type { SaveSearchDefaults } from '@/src/components/ListingSearch/SaveSearchModal';
// Imported statically, not through next/dynamic. A lazy boundary here resolves after a
// synchronous test tears jsdom down, and React then commits into a missing `window` — an
// unhandled error that fails vitest with every test still passing (PR 21599). It also bought
// nothing: everything this tool imports is already in the SRP bundle via this file.
import { WebMcpSaveSearchTool } from './WebMcpSaveSearchTool';
import { emitTrackCreateSearchAlertEvent, emitTrackOpenSearchAlertModalEvent } from '@/src/analytics/hotpads/events';
import type { SaveSearchTriggerLocation } from '@/src/analytics/hotpads/events';

// Rendered only once open, so the form and its Constellation Select stay out of
// the layout chunk every route in this group now loads.
const SaveSearchModal = dynamic(() =>
  import('@/src/components/ListingSearch/SaveSearchModal').then((m) => m.SaveSearchModal),
);
const EditSavedSearchModal = dynamic(() =>
  import('@/src/components/ListingSearch/EditSavedSearchModal').then((m) => m.EditSavedSearchModal),
);

const PERSISTED_SAVED_SEARCH_KEY = 'hp:saved-search-match-key';

function readPersistedSavedSearchKey(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
}

function persistSavedSearchKey(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(PERSISTED_SAVED_SEARCH_KEY, key);
}

function clearPersistedSavedSearchKey(key?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const currentKey = window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
  if (key && currentKey !== key) {
    return;
  }

  window.sessionStorage.removeItem(PERSISTED_SAVED_SEARCH_KEY);
}

type SrpSaveSearchContextValue = {
  isSaved: boolean;
  isDisabled: boolean;
  label: string;
  accessibleLabel: string;
  /**
   * Whether a click opens a dialog rather than toggling state in place. True
   * everywhere except a guest's optimistic save, which is the only path that
   * still flips the button on its own — see `handleToggle`.
   */
  opensDialog: boolean;
  handleToggle: (triggerLocation: SaveSearchTriggerLocation) => void;
};

export const INERT_SAVE_SEARCH_CONTEXT: SrpSaveSearchContextValue = {
  isSaved: false,
  isDisabled: true,
  label: 'Save',
  accessibleLabel: 'Save search',
  opensDialog: true,
  handleToggle: () => undefined,
};

const SrpSaveSearchContext = createContext<SrpSaveSearchContextValue | null>(null);

/**
 * Mounted once by the `(search)` layout so the Save button can live in the
 * sticky search bar, which outlives any one route. The route it is showing
 * seeds `useSaveSearchTarget`; until one does, the button is inert.
 */
export function SrpSaveSearchProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('search');
  const { filters } = useFilters();
  const { data: target } = useSaveSearchTarget();
  const { data: currentUser } = useCurrentUser();
  const hasResolvedCurrentUser = currentUser !== undefined;
  const isLoggedIn = currentUser?.loggedIn ?? false;
  // Also gated on a target: this provider mounts on every route in the search
  // layout, and without one there is nothing to match against.
  const savedSearchesQuery = useSavedSearches({ enabled: isLoggedIn && !!target });
  const fetchSavedSearches = useFetchSavedSearches();
  // '' without a target: sessionStorage never holds an empty key, so the
  // persisted-save restore below cannot match on one.
  const savedSearchMatchKey = useMemo(
    () => (target ? buildSavedSearchMatchKey({ ...target, filters }) : ''),
    [filters, target],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dialogDefaults, setDialogDefaults] = useState<SaveSearchDefaults | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [optimisticSaved, setOptimisticSaved] = useState(false);
  // The dialog is on its sign-in code step, holding the search the renter already submitted.
  const [isSigningIn, setIsSigningIn] = useState(false);

  // This provider outlives every route in the shell, so none of the transient
  // state above may: a new target means a different search, and carrying the
  // old one over renders another area's Saved state, or reopens its modal.
  // Declared ahead of the reconciliation effects so they get the last word on
  // the incoming target.
  useEffect(() => {
    setIsModalOpen(false);
    setIsSigningIn(false);
    setIsEditOpen(false);
    setDialogDefaults(null);
    setOptimisticSaved(false);
  }, [target]);

  const matchingSearch = useMemo(
    () => (target ? findMatchingSavedSearch(savedSearchesQuery.data, { ...target, filters }) : undefined),
    [filters, savedSearchesQuery.data, target],
  );

  // Defaults come from `save_search`, which proposes the name and cadence the dialog would
  // otherwise ask for. A human click passes none and gets the plain defaults.
  const openSaveSearchModal = useCallback((defaults?: SaveSearchDefaults) => {
    setDialogDefaults(defaults ?? null);
    setIsModalOpen(true);
  }, []);

  // Shared by the dialog and by `save_search`. Both emit the same clickstream event:
  // its triggerObject is a registered vocabulary with no agent-specific value, and what the event
  // models — an alert created from the SRP — is true either way.
  const handleSearchSaved = useCallback(() => {
    emitTrackCreateSearchAlertEvent({
      triggerLocation: 'search_results',
      triggerObject: 'search_results|alerts_modal',
    });
    persistSavedSearchKey(savedSearchMatchKey);
    setOptimisticSaved(true);
  }, [savedSearchMatchKey]);

  // A guest who signs in to save may already have this search on the account; the Save button
  // could not know that while they were signed out. Best-effort: a failed read saves anyway.
  const isSearchAlreadySaved = useCallback(async () => {
    if (!target) return false;
    try {
      const savedSearches = await fetchSavedSearches();
      return Boolean(findMatchingSavedSearch(savedSearches, { ...target, filters }));
    } catch {
      return false;
    }
  }, [fetchSavedSearches, filters, target]);

  useEffect(() => {
    if (!target) return;

    // Every save signs in first, so a key can only be a signed-in account's; a confirmed guest
    // restoring it would show Saved for a search they never saved (HP-9230).
    const mayRestore = !hasResolvedCurrentUser || isLoggedIn;
    if (mayRestore && readPersistedSavedSearchKey() === savedSearchMatchKey) {
      setOptimisticSaved(true);
      return;
    }

    if (!matchingSearch && hasResolvedCurrentUser) {
      setOptimisticSaved(false);
    }
  }, [hasResolvedCurrentUser, isLoggedIn, matchingSearch, savedSearchMatchKey, target]);

  useEffect(() => {
    if (!target) return;

    if (matchingSearch) {
      persistSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(true);
      return;
    }

    if (hasResolvedCurrentUser && !isLoggedIn) {
      clearPersistedSavedSearchKey();
      setOptimisticSaved(false);
      return;
    }

    if (isLoggedIn && !savedSearchesQuery.isPending && !savedSearchesQuery.isFetching) {
      clearPersistedSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(false);
    }
  }, [
    hasResolvedCurrentUser,
    isLoggedIn,
    matchingSearch,
    savedSearchMatchKey,
    savedSearchesQuery.isFetching,
    savedSearchesQuery.isPending,
    target,
  ]);

  const isSaved = !!matchingSearch || optimisticSaved;
  const isWaitingForSavedState =
    (!hasResolvedCurrentUser && optimisticSaved) ||
    (isLoggedIn && savedSearchesQuery.isPending && savedSearchesQuery.data === undefined) ||
    (isLoggedIn && optimisticSaved && !matchingSearch);
  const isDisabled = !target || !hasResolvedCurrentUser || isWaitingForSavedState;

  const handleToggle = useCallback(
    async (triggerLocation: SaveSearchTriggerLocation) => {
      await interactionResponse();

      if (isDisabled) {
        return;
      }

      // Already saved: edit it. Deleting used to happen here, on one click of the
      // button, and now lives behind the modal's own delete action (HP-9043).
      if (matchingSearch) {
        setIsEditOpen(true);
        return;
      }

      // A guest's save is optimistic only — there is no stored search to edit, so
      // the button stays a toggle for them.
      if (!isLoggedIn && optimisticSaved) {
        clearPersistedSavedSearchKey(savedSearchMatchKey);
        setOptimisticSaved(false);
        return;
      }

      emitTrackOpenSearchAlertModalEvent(triggerLocation);
      openSaveSearchModal();
    },
    [isDisabled, isLoggedIn, matchingSearch, openSaveSearchModal, optimisticSaved, savedSearchMatchKey],
  );

  // Three states, not two. The accessible name has to start with the visible
  // label (WCAG 2.5.3) and say what the click actually does, and since HP-9043
  // a stored search opens the edit modal instead of being removed outright.
  // Only a guest's optimistic save still removes on click.
  const accessibleLabel = !isSaved
    ? t('saveSearch')
    : matchingSearch
      ? t('savedSearchEditAction')
      : t('savedSearchRemoveAction');

  const value = useMemo<SrpSaveSearchContextValue>(
    () => ({
      isSaved,
      isDisabled,
      label: isSaved ? t('savedSearch') : t('saveSearchLabel'),
      accessibleLabel,
      opensDialog: !isSaved || !!matchingSearch,
      handleToggle,
    }),
    [accessibleLabel, handleToggle, isDisabled, isSaved, matchingSearch, t],
  );

  return (
    <SrpSaveSearchContext.Provider value={value}>
      {children}
      {target ? (
        <WebMcpSaveSearchTool
          isSaved={isSaved}
          isSavedStateResolving={isWaitingForSavedState}
          isSigningIn={isSigningIn}
          openDialog={openSaveSearchModal}
          onSaved={handleSearchSaved}
        />
      ) : null}
      {target && isModalOpen ? (
        <SaveSearchModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setIsSigningIn(false);
          }}
          onSaved={handleSearchSaved}
          onSignInStepChange={setIsSigningIn}
          isSearchAlreadySaved={isSearchAlreadySaved}
          legacyAreaId={target.legacyAreaId}
          defaultName={target.defaultName}
          defaults={dialogDefaults ?? undefined}
          areaBounds={target.areaBounds}
          searchScope={target.searchScope}
        />
      ) : null}
      {target && matchingSearch && isEditOpen ? (
        <EditSavedSearchModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          savedSearch={matchingSearch}
          defaultName={target.defaultName}
          onDeleted={() => {
            clearPersistedSavedSearchKey(savedSearchMatchKey);
            setOptimisticSaved(false);
          }}
        />
      ) : null}
    </SrpSaveSearchContext.Provider>
  );
}

export function useOptionalSrpSaveSearch(): SrpSaveSearchContextValue | null {
  return useContext(SrpSaveSearchContext);
}

export function useSrpSaveSearch(): SrpSaveSearchContextValue {
  const context = useOptionalSrpSaveSearch();

  return context ?? INERT_SAVE_SEARCH_CONTEXT;
}
